/**
 * FAQ Management Page (Admin Only)
 *
 * Provides system administrators with tools to:
 * - Manage FAQ categories (create, edit, delete, reorder)
 * - Manage FAQ questions (create, edit, delete, publish/draft)
 * - Search and filter questions
 * - View statistics (views count)
 */

import React, { useState, useEffect } from 'react';
import { useTranslation, type TFunction } from 'react-i18next';
import { Card } from '../../components/ui/Card';
import { Alert } from '../../components/ui/Alert';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { Table, type TableColumn } from '../../components/ui/Table';
import { Dropdown, type DropdownItem } from '../../components/ui/Dropdown';
import { Pagination } from '../../components/ui/Pagination';
import { Modal } from '../../components/ui/Modal';
import { ConfirmationModal } from '../../components/ui/ConfirmationModal';
import { Tabs, type Tab } from '../../components/ui/Tabs';
import { faqCategoryAdminService, faqQuestionAdminService } from '../../services/admin/faq';
import type { FaqCategory, FaqQuestion, FaqStatus } from '../../types/faq';

type TabType = 'questions' | 'categories';

// Supported languages for translations
const SUPPORTED_LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Español' },
  { code: 'de', name: 'Deutsch' },
  { code: 'ru', name: 'Русский' },
];

export const FaqManagementPage: React.FC = () => {
  const { t } = useTranslation('admin');
  const [activeTab, setActiveTab] = useState<TabType>('questions');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Clear alerts after 5 seconds
  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess(null);
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">{t('faq_management.page_title')}</h1>
        <p className="text-gray-600 mt-1">{t('faq_management.page_subtitle')}</p>
      </div>

      {/* Alerts */}
      {success && (
        <Alert variant="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}
      {error && (
        <Alert variant="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('questions')}
            className={`${
              activeTab === 'questions'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            {t('faq_management.table.question')}
          </button>
          <button
            onClick={() => setActiveTab('categories')}
            className={`${
              activeTab === 'categories'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            {t('faq_management.category_filter_label')}
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'questions' ? (
        <QuestionsTab setError={setError} setSuccess={setSuccess} t={t} />
      ) : (
        <CategoriesTab setError={setError} setSuccess={setSuccess} t={t} />
      )}
    </div>
  );
};

// ===== Questions Tab =====
interface TabProps {
  setError: (error: string | null) => void;
  setSuccess: (success: string | null) => void;
  t: TFunction;
}

function QuestionsTab({ setError, setSuccess, t }: TabProps) {
  const [questions, setQuestions] = useState<FaqQuestion[]>([]);
  const [categories, setCategories] = useState<FaqCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<number | ''>('');
  const [statusFilter, setStatusFilter] = useState<FaqStatus | ''>('');
  const [showModal, setShowModal] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<FaqQuestion | null>(null);
  const [viewQuestion, setViewQuestion] = useState<FaqQuestion | null>(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; questionId: number | null; questionText: string | null }>({
    open: false,
    questionId: null,
    questionText: null,
  });

  // Fetch questions
  const fetchQuestions = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page: currentPage,
        ...(search && { search }),
        ...(categoryFilter && { category_id: categoryFilter }),
        ...(statusFilter && { status: statusFilter }),
      };

      const response = await faqQuestionAdminService.getAllQuestions(params);
      setQuestions(response.data);
      setTotalPages(response.last_page);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('faq_management.messages.load_error'));
    } finally {
      setLoading(false);
    }
  };

  // Fetch categories for filter
  const fetchCategories = async () => {
    try {
      const cats = await faqCategoryAdminService.getAllCategories();
      setCategories(cats);
    } catch (err) {
      console.error('Failed to load categories:', err);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchQuestions();
  }, [currentPage, search, categoryFilter, statusFilter]);

  // Handle delete
  const handleDelete = async (id: number, question: string) => {
    setDeleteConfirm({ open: true, questionId: id, questionText: question });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm.questionId) return;

    try {
      await faqQuestionAdminService.deleteQuestion(deleteConfirm.questionId);
      setSuccess(t('faq_management.messages.faq_deleted'));
      fetchQuestions();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('faq_management.messages.delete_error'));
    }
  };

  // Table columns
  const columns: TableColumn<FaqQuestion>[] = [
    {
      key: 'id',
      header: t('faq_management.table.id'),
    },
    {
      key: 'question',
      header: t('faq_management.table.question'),
      render: (value) => (
        <div className="max-w-md truncate" title={String(value)}>
          {String(value)}
        </div>
      ),
    },
    {
      key: 'category',
      header: t('faq_management.table.category'),
      render: (value) => {
        const category = value as FaqQuestion['category'];
        return category ? category.name : 'N/A';
      },
    },
    {
      key: 'status',
      header: t('faq_management.table.status'),
      render: (value) => (
        <Badge variant={value === 'published' ? 'success' : 'warning'}>
          {value === 'published' ? t('faq_management.table.published') : t('faq_management.table.draft')}
        </Badge>
      ),
    },
    {
      key: 'views_count',
      header: t('faq_management.table.views'),
    },
    {
      key: 'order',
      header: t('faq_management.table.order'),
    },
  ];

  // Actions dropdown
  const renderActions = (question: FaqQuestion) => {
    const items: DropdownItem[] = [
      {
        id: 'view',
        label: t('faq_management.actions.view'),
        onClick: () => {
          setViewQuestion(question);
          setShowViewModal(true);
        },
      },
      {
        id: 'edit',
        label: t('faq_management.actions.edit'),
        onClick: () => {
          setEditingQuestion(question);
          setShowModal(true);
        },
      },
      {
        id: 'delete',
        label: t('faq_management.actions.delete'),
        onClick: () => handleDelete(question.id, question.question),
        danger: true,
      },
    ];

    return (
      <Dropdown
        trigger={
          <Button variant="secondary" size="sm">
            {t('faq_management.actions.label')}
          </Button>
        }
        items={items}
        align="right"
      />
    );
  };

  return (
    <>
      <Card>
        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-6">
          <div className="flex-1 min-w-[200px]">
            <Input
              type="text"
              placeholder={t('faq_management.search_placeholder')}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>
          <div className="w-48">
            <select
              value={categoryFilter}
              onChange={(e) => {
                setCategoryFilter(e.target.value ? Number(e.target.value) : '');
                setCurrentPage(1);
              }}
              className="input"
            >
              <option value="">{t('faq_management.category_filter_all')}</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>
          <div className="w-32">
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as FaqStatus | '');
                setCurrentPage(1);
              }}
              className="input"
            >
              <option value="">{t('faq_management.category_filter_all')}</option>
              <option value="published">{t('faq_management.table.published')}</option>
              <option value="draft">{t('faq_management.table.draft')}</option>
            </select>
          </div>
          <Button
            onClick={() => {
              setEditingQuestion(null);
              setShowModal(true);
            }}
          >
            {t('faq_management.create_button')}
          </Button>
        </div>

        {/* Table */}
        <Table
          columns={columns}
          data={questions}
          loading={loading}
          renderActions={renderActions}
        />

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </div>
        )}
      </Card>

      {/* Create/Edit Modal */}
      {showModal && (
        <QuestionModal
          question={editingQuestion}
          categories={categories}
          onClose={() => {
            setShowModal(false);
            setEditingQuestion(null);
          }}
          onSuccess={(message) => {
            setSuccess(message);
            setShowModal(false);
            setEditingQuestion(null);
            fetchQuestions();
          }}
          onError={setError}
          t={t}
        />
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        open={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ open: false, questionId: null, questionText: null })}
        onConfirm={confirmDelete}
        title={t('faq_management.actions.delete')}
        message={t('faq_management.actions.delete_confirm')}
        description={deleteConfirm.questionText ? deleteConfirm.questionText.substring(0, 100) : undefined}
        confirmText={t('faq_management.actions.delete')}
        variant="danger"
      />

      {/* View Modal */}
      {showViewModal && viewQuestion && (
        <Modal
          open={true}
          onClose={() => {
            setShowViewModal(false);
            setViewQuestion(null);
          }}
          title={t('faq_management.view_modal.title')}
          size="xlarge"
        >
          <div className="space-y-4">
            {/* Question Info */}
            <div className="border-b border-gray-200 pb-3">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-gray-700">{t('faq_management.view_modal.id_label')}</span>{' '}
                  <span className="text-gray-900">{viewQuestion.id}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">{t('faq_management.view_modal.category_label')}</span>{' '}
                  <span className="text-gray-900">{viewQuestion.category?.name || 'N/A'}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">{t('faq_management.view_modal.status_label')}</span>{' '}
                  <Badge variant={viewQuestion.status === 'published' ? 'success' : 'warning'}>
                    {viewQuestion.status === 'published' ? t('faq_management.table.published') : t('faq_management.table.draft')}
                  </Badge>
                </div>
                <div>
                  <span className="font-medium text-gray-700">{t('faq_management.view_modal.views_label')}</span>{' '}
                  <span className="text-gray-900">{viewQuestion.views_count || 0}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">{t('faq_management.view_modal.order_label')}</span>{' '}
                  <span className="text-gray-900">{viewQuestion.order}</span>
                </div>
              </div>
            </div>

            {/* Question */}
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-2">{t('faq_management.view_modal.question_section')}</h4>
              <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
                <p className="text-gray-800">{viewQuestion.question}</p>
              </div>
            </div>

            {/* Answer */}
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-2">{t('faq_management.view_modal.answer_section')}</h4>
              <div className="bg-gray-50 border border-gray-200 rounded-md p-4 overflow-auto max-h-96">
                <pre className="text-sm text-gray-800 whitespace-pre-wrap break-words font-sans">
                  {viewQuestion.answer}
                </pre>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {t('faq_management.view_modal.markdown_note')}
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-2 border-t border-gray-200">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowViewModal(false);
                  setEditingQuestion(viewQuestion);
                  setShowModal(true);
                }}
              >
                {t('faq_management.view_modal.edit_button')}
              </Button>
              <Button
                variant="danger"
                onClick={() => {
                  setShowViewModal(false);
                  handleDelete(viewQuestion.id, viewQuestion.question);
                }}
              >
                {t('faq_management.view_modal.delete_button')}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

// ===== Categories Tab =====
function CategoriesTab({ setError, setSuccess, t }: TabProps) {
  const [categories, setCategories] = useState<FaqCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<FaqCategory | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; categoryId: number | null; categoryName: string | null }>({
    open: false,
    categoryId: null,
    categoryName: null,
  });

  // Fetch categories
  const fetchCategories = async () => {
    try {
      setLoading(true);
      setError(null);

      const cats = await faqCategoryAdminService.getAllCategories();
      setCategories(cats);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('faq_management.messages.load_error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  // Handle delete
  const handleDelete = async (id: number, name: string) => {
    setDeleteConfirm({ open: true, categoryId: id, categoryName: name });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm.categoryId) return;

    try {
      await faqCategoryAdminService.deleteCategory(deleteConfirm.categoryId);
      setSuccess(t('faq_management.messages.faq_deleted'));
      fetchCategories();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('faq_management.messages.delete_error'));
    }
  };

  // Table columns
  const columns: TableColumn<FaqCategory>[] = [
    {
      key: 'id',
      header: t('faq_management.table.id'),
    },
    {
      key: 'name',
      header: t('faq_management.table.name'),
    },
    {
      key: 'slug',
      header: t('faq_management.table.slug'),
    },
    {
      key: 'description',
      header: t('faq_management.table.description'),
      render: (value) => (
        <div className="max-w-md truncate" title={String(value || '')}>
          {String(value || 'N/A')}
        </div>
      ),
    },
    {
      key: 'order',
      header: t('faq_management.table.order'),
    },
    {
      key: 'questions_count',
      header: t('faq_management.table.questions_count'),
      render: (value) => value || 0,
    },
  ];

  // Actions dropdown
  const renderActions = (category: FaqCategory) => {
    const items: DropdownItem[] = [
      {
        id: 'edit',
        label: t('faq_management.actions.edit'),
        onClick: () => {
          setEditingCategory(category);
          setShowModal(true);
        },
      },
      {
        id: 'delete',
        label: t('faq_management.actions.delete'),
        onClick: () => handleDelete(category.id, category.name),
        danger: true,
      },
    ];

    return (
      <Dropdown
        trigger={
          <Button variant="secondary" size="sm">
            {t('faq_management.actions.label')}
          </Button>
        }
        items={items}
        align="right"
      />
    );
  };

  return (
    <>
      <Card>
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{t('faq_management.category_filter_label')}</h2>
            <p className="text-sm text-gray-600">{t('faq_management.page_subtitle')}</p>
          </div>
          <Button
            onClick={() => {
              setEditingCategory(null);
              setShowModal(true);
            }}
          >
            {t('faq_management.create_button')}
          </Button>
        </div>

        {/* Table */}
        <Table
          columns={columns}
          data={categories}
          loading={loading}
          renderActions={renderActions}
        />
      </Card>

      {/* Create/Edit Modal */}
      {showModal && (
        <CategoryModal
          category={editingCategory}
          onClose={() => {
            setShowModal(false);
            setEditingCategory(null);
          }}
          onSuccess={(message) => {
            setSuccess(message);
            setShowModal(false);
            setEditingCategory(null);
            fetchCategories();
          }}
          onError={setError}
          t={t}
        />
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        open={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ open: false, categoryId: null, categoryName: null })}
        onConfirm={confirmDelete}
        title={t('faq_management.actions.delete')}
        message={t('faq_management.actions.delete_confirm')}
        description={deleteConfirm.categoryName ? `${t('faq_management.category_form.category_prefix')} ${deleteConfirm.categoryName}` : undefined}
        confirmText={t('faq_management.actions.delete')}
        variant="danger"
      />
    </>
  );
}

// ===== Category Modal =====
interface CategoryModalProps {
  category: FaqCategory | null;
  onClose: () => void;
  onSuccess: (message: string) => void;
  onError: (error: string) => void;
  t: TFunction;
}

function CategoryModal({ category, onClose, onSuccess, onError, t }: CategoryModalProps) {
  const [formData, setFormData] = useState({
    name: category?.name || '',
    slug: category?.slug || '',
    description: category?.description || '',
    order: category?.order || 0,
    name_translations: category?.name_translations || {},
    description_translations: category?.description_translations || {},
  });
  const [submitting, setSubmitting] = useState(false);

  // Update form data when category prop changes (for edit mode)
  useEffect(() => {
    if (category) {
      setFormData({
        name: category.name,
        slug: category.slug || '',
        description: category.description || '',
        order: category.order,
        name_translations: category.name_translations || {},
        description_translations: category.description_translations || {},
      });
    }
  }, [category]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (category) {
        await faqCategoryAdminService.updateCategory(category.id, formData);
        onSuccess(t('faq_management.messages.faq_updated'));
      } else {
        await faqCategoryAdminService.createCategory(formData);
        onSuccess(t('faq_management.messages.faq_created'));
      }
    } catch (err) {
      onError(err instanceof Error ? err.message : t('faq_management.messages.create_error'));
    } finally {
      setSubmitting(false);
    }
  };

  const updateTranslation = (field: 'name_translations' | 'description_translations', lang: string, value: string) => {
    setFormData({
      ...formData,
      [field]: {
        ...formData[field],
        [lang]: value,
      },
    });
  };

  const tabs: Tab[] = [
    {
      id: 'default',
      label: t('faq_management.category_form.tab_default'),
      content: (
        <div className="space-y-4">
          <div>
            <label className="label">{t('faq_management.category_form.name_default_label')}</label>
            <Input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder={t('faq_management.category_form.name_placeholder')}
              required
            />
          </div>

          <div>
            <label className="label">{t('faq_management.category_form.slug_label')}</label>
            <Input
              type="text"
              value={formData.slug}
              onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
              placeholder={t('faq_management.category_form.slug_placeholder')}
            />
            <p className="text-xs text-gray-500 mt-1">
              {t('faq_management.category_form.slug_help')}
            </p>
          </div>

          <div>
            <label className="label">{t('faq_management.category_form.description_default_label')}</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder={t('faq_management.category_form.description_placeholder')}
              rows={3}
              className="input"
            />
          </div>

          <div>
            <label className="label">{t('faq_management.form.order_label')}</label>
            <Input
              type="number"
              value={formData.order}
              onChange={(e) => setFormData({ ...formData, order: Number(e.target.value) })}
              min={0}
            />
            <p className="text-xs text-gray-500 mt-1">
              {t('faq_management.category_form.order_help')}
            </p>
          </div>
        </div>
      ),
    },
    ...SUPPORTED_LANGUAGES.map((lang) => ({
      id: lang.code,
      label: lang.name,
      content: (
        <div className="space-y-4" key={lang.code}>
          <div>
            <label className="label">{t('faq_management.category_form.name_language_label')} ({lang.name})</label>
            <Input
              type="text"
              value={(formData.name_translations as any)?.[lang.code] || ''}
              onChange={(e) => updateTranslation('name_translations', lang.code, e.target.value)}
              placeholder={`${t('faq_management.category_form.name_placeholder')} (${lang.name})`}
            />
            <p className="text-xs text-gray-500 mt-1">
              {t('faq_management.category_form.name_language_help')}
            </p>
          </div>

          <div>
            <label className="label">{t('faq_management.category_form.description_language_label')} ({lang.name})</label>
            <textarea
              value={(formData.description_translations as any)?.[lang.code] || ''}
              onChange={(e) => updateTranslation('description_translations', lang.code, e.target.value)}
              placeholder={`${t('faq_management.category_form.description_placeholder')} (${lang.name})`}
              rows={3}
              className="input"
            />
            <p className="text-xs text-gray-500 mt-1">
              {t('faq_management.category_form.description_language_help')}
            </p>
          </div>
        </div>
      ),
    })),
  ];

  return (
    <Modal
      open={true}
      onClose={onClose}
      title={category ? t('faq_management.actions.edit') : t('faq_management.create_button')}
      size="xlarge"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <Tabs tabs={tabs} defaultTab="default" />

        <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
          <Button type="button" variant="secondary" onClick={onClose}>
            {t('faq_management.form.cancel_button')}
          </Button>
          <Button type="submit" disabled={submitting}>
            {submitting ? t('faq_management.form.saving') : t('faq_management.form.save_button')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// ===== Question Modal =====
interface QuestionModalProps {
  question: FaqQuestion | null;
  categories: FaqCategory[];
  onClose: () => void;
  onSuccess: (message: string) => void;
  onError: (error: string) => void;
  t: TFunction;
}

function QuestionModal({ question, categories, onClose, onSuccess, onError, t }: QuestionModalProps) {
  const [formData, setFormData] = useState({
    category_id: question?.category_id || (categories[0]?.id || 0),
    question: question?.question || '',
    answer: question?.answer || '',
    status: question?.status || ('draft' as FaqStatus),
    order: question?.order || 0,
    question_translations: question?.question_translations || {},
    answer_translations: question?.answer_translations || {},
  });
  const [submitting, setSubmitting] = useState(false);

  // Update form data when question prop changes (for edit mode)
  useEffect(() => {
    if (question) {
      setFormData({
        category_id: question.category_id,
        question: question.question,
        answer: question.answer,
        status: question.status,
        order: question.order,
        question_translations: question.question_translations || {},
        answer_translations: question.answer_translations || {},
      });
    }
  }, [question]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (question) {
        await faqQuestionAdminService.updateQuestion(question.id, formData);
        onSuccess(t('faq_management.messages.faq_updated'));
      } else {
        await faqQuestionAdminService.createQuestion(formData);
        onSuccess(t('faq_management.messages.faq_created'));
      }
    } catch (err) {
      onError(err instanceof Error ? err.message : t('faq_management.messages.create_error'));
    } finally {
      setSubmitting(false);
    }
  };

  const updateTranslation = (field: 'question_translations' | 'answer_translations', lang: string, value: string) => {
    setFormData({
      ...formData,
      [field]: {
        ...formData[field],
        [lang]: value,
      },
    });
  };

  const tabs: Tab[] = [
    {
      id: 'default',
      label: t('faq_management.question_form.tab_default'),
      content: (
        <div className="space-y-4">
          <div>
            <label className="label">{t('faq_management.form.category_label')} *</label>
            <select
              value={formData.category_id}
              onChange={(e) => setFormData({ ...formData, category_id: Number(e.target.value) })}
              className="input"
              required
            >
              <option value="">{t('faq_management.question_form.select_category')}</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="label">{t('faq_management.question_form.question_default_label')}</label>
            <Input
              type="text"
              value={formData.question}
              onChange={(e) => setFormData({ ...formData, question: e.target.value })}
              placeholder={t('faq_management.question_form.question_placeholder')}
              required
            />
          </div>

          <div>
            <label className="label">{t('faq_management.question_form.answer_default_label')}</label>
            <textarea
              value={formData.answer}
              onChange={(e) => setFormData({ ...formData, answer: e.target.value })}
              placeholder={t('faq_management.question_form.answer_placeholder')}
              rows={12}
              className="input font-mono text-sm"
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              {t('faq_management.question_form.markdown_help')}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">{t('faq_management.form.status_label')} *</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value as FaqStatus })}
                className="input"
              >
                <option value="draft">{t('faq_management.table.draft')}</option>
                <option value="published">{t('faq_management.table.published')}</option>
              </select>
            </div>

            <div>
              <label className="label">{t('faq_management.form.order_label')}</label>
              <Input
                type="number"
                value={formData.order}
                onChange={(e) => setFormData({ ...formData, order: Number(e.target.value) })}
                min={0}
              />
            </div>
          </div>
        </div>
      ),
    },
    ...SUPPORTED_LANGUAGES.map((lang) => ({
      id: lang.code,
      label: lang.name,
      content: (
        <div className="space-y-4" key={lang.code}>
          <div>
            <label className="label">{t('faq_management.question_form.question_language_label')} ({lang.name})</label>
            <Input
              type="text"
              value={(formData.question_translations as any)?.[lang.code] || ''}
              onChange={(e) => updateTranslation('question_translations', lang.code, e.target.value)}
              placeholder={`${t('faq_management.question_form.question_placeholder')} (${lang.name})`}
            />
            <p className="text-xs text-gray-500 mt-1">
              {t('faq_management.question_form.question_language_help')}
            </p>
          </div>

          <div>
            <label className="label">{t('faq_management.question_form.answer_language_label')} ({lang.name})</label>
            <textarea
              value={(formData.answer_translations as any)?.[lang.code] || ''}
              onChange={(e) => updateTranslation('answer_translations', lang.code, e.target.value)}
              placeholder={`${t('faq_management.question_form.answer_placeholder')} (${lang.name})`}
              rows={12}
              className="input font-mono text-sm"
            />
            <p className="text-xs text-gray-500 mt-1">
              {t('faq_management.question_form.answer_language_help')}
            </p>
          </div>
        </div>
      ),
    })),
  ];

  return (
    <Modal
      open={true}
      onClose={onClose}
      title={question ? t('faq_management.actions.edit') : t('faq_management.create_button')}
      size="xlarge"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <Tabs tabs={tabs} defaultTab="default" />

        <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
          <Button type="button" variant="secondary" onClick={onClose}>
            {t('faq_management.form.cancel_button')}
          </Button>
          <Button type="submit" disabled={submitting}>
            {submitting ? t('faq_management.form.saving') : t('faq_management.form.save_button')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
